/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck - Material Tailwind component prop warnings

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useForm } from 'react-hook-form';
import GlobalSearch from '@/components/atoms/GlobalSearch';
import { useDebounce } from '@/hooks/useDebounce';
import FullPageCard from '@/components/organism/Cards/FullPageCard';
import { isEmpty } from 'lodash';
import { DataTable } from '@/components/organism/Table';
import {
  EReportFormat,
  EReportLabel,
} from '@/constant/downloadReport/downloadReport.const';
import { API_URL_CONFIG } from '@/config/urlConfig/apiUrlConfig';
import DownloadReport from '@/components/organism/ExportDataModal/DownloadReport';
import { ISmartCoolerProduct } from '@/services/smartCoolerProducts';
import {
  fetchApprovedProducts,
  IApprovedProduct,
} from '@/services/standalone/approvedProducts';
import { useToast } from '@/components/organism/Toast/ToastProvider';
import Loading from '@/components/atoms/Loader';
import { getSelectedTimeZone } from '@/utils/dateTime';
import {
  pagination,
  STRINGS,
  ROW_PER_PAGE_OPTION,
  COMMON_VALIDATIONS,
} from '@/constant/global/global.constant';
import { LuPackage } from 'react-icons/lu';
import { trimSpacesInPayload } from '@/utils/stringFormatting';
import useDeepEffect from '@/hooks/useDeepEffect';
import { PROVIDER_CONSTANT_PROPS } from '@/constant/provider/provider';
import { useScreenClickDetector } from '@/hooks/useScreenClickDetector';
import {
  EAllowedFieldsForExport,
  EExportPage,
} from '@/components/organism/ExportDataModal/export.types';
import {
  deleteCookie,
  getStandaloneParams,
  handleErrorClose,
} from '@/standalone';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import ConfirmActionModal from '@/components/organism/ConfirmActionModal';
import { NAV_ITEMS, getNavItemIcon } from '@/constant/layout/layout.constant';
import {
  EStandalonePages,
  MAGIC_COOKIES,
  STANDALONE_CONSTANTS,
  SUBAPP_CONFIGS,
  APPROVED_PRODUCTS_PROPS,
} from '../constants';
import { buildColumns } from './approvedProducts.columns';
import { APPROVED_SMART_COOLER_PRODUCTS_TEST_IDS as TID } from '@/constant/testIds/approvedSmartCoolerProducts.testIds';
import { tid } from '@/utils/testId';

const ApprovedProducts: React.FC = () => {
  // API error state
  const [apiError, setApiError] = useState<string | null>(null);
  const [isAuthError, setIsAuthError] = useState(false);
  const hasLoaded = useRef(false);

  const [isOpen, setIsOpen] = useState(false);
  const [isCopy, setIsCopy] = useState(false);
  const [createdStartDate, setStartDate] = useState<string>('');
  const [createdEndDate, setStartEndDate] = useState<string>('');
  const [isDeleted, setIsDeleted] = useState<boolean | undefined>(false);
  const [isStateMutated, setIsStateMutated] = useState(false);
  const [searchByProductName, onSearchByProductName] = useState('');
  const [barcode, onSearchByBarcode] = useState('');
  const [productIdIdentifier, setProductIdIdentifier] = useState('');
  const [sorting, setSorting] = useState({
    orderBy: 'updatedOn',
    order: 'desc',
  });
  const [searchApplication, setSearchApplication] = useState('');
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [activateConfirmOpen, setActivateConfirmOpen] = useState(false);

  const {
    register,
    formState: { errors },
    getValues,
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      deleteConfirm: '',
      activateConfirm: '',
    },
  });

  const selectedTimezone = getSelectedTimeZone();

  const [smartCoolerProductsPagination, setSmartCoolerProductsPagination] =
    useState({
      limit: ROW_PER_PAGE_OPTION[1],
      page: pagination.page,
    });

  // Debounced search queries
  const barcodeQuery = useDebounce(
    barcode,
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );
  const productIdIdentifierQuery = useDebounce(
    productIdIdentifier,
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );
  const searchByProductNameQuery = useDebounce(
    searchByProductName,
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );

  const toast = useToast();

  // Approved products state
  const [approvedProducts, setApprovedProducts] = useState<
    ISmartCoolerProduct[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [paginationResponse, setPaginationResponse] = useState<{
    totalRecords: number;
  }>({ totalRecords: 0 });

  // Get params for API call
  const getApprovedProductParams = () => {
    const formattedPayload = trimSpacesInPayload({
      ...(barcodeQuery && barcodeQuery.length > 0
        ? { barcode: barcodeQuery }
        : {}),
      ...(productIdIdentifierQuery && productIdIdentifierQuery.length > 0
        ? { productIdIdentifier: productIdIdentifierQuery }
        : {}),
      ...(searchByProductNameQuery && searchByProductNameQuery.length > 0
        ? { name: searchByProductNameQuery }
        : {}),
    });
    const formattedOrderByValue =
      sorting.orderBy === 'shortProductName' ? 'name' : sorting.orderBy;
    const approvedProductsParams = {
      ...smartCoolerProductsPagination,
      ...sorting,
      orderBy: formattedOrderByValue,
      ...formattedPayload,
      inactive: isDeleted,
    };
    return approvedProductsParams;
  };

  // Fetch approved products from API
  const fetchApprovedProductsList = async (params: any) => {
    setIsLoading(true);
    try {
      const { sid, sig } = getStandaloneParams();
      if (sid && sig) {
        const response = await fetchApprovedProducts(params);

        if (response.data.success) {
          setApprovedProducts(response.data.data || []);
          setPaginationResponse({
            totalRecords:
              response.data.pagination?.totalRecords ||
              response.data.data?.length ||
              0,
          });
          hasLoaded.current = true;
        } else {
          setApiError(
            STANDALONE_CONSTANTS.PRODUCTS_LOAD_ERROR_MODAL
              .API_ERROR_DESCRIPTION,
          );
        }
      } else {
        setIsAuthError(true);
      }
    } catch (err: any) {
      console.error('Error fetching approved products:', err);
      setApiError(
        STANDALONE_CONSTANTS.PRODUCTS_LOAD_ERROR_MODAL
          .UNEXPECTED_ERROR_DESCRIPTION,
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Track state mutation for debounced API calls
  useDeepEffect(() => {
    setIsStateMutated(true);
  }, [
    smartCoolerProductsPagination,
    barcodeQuery,
    productIdIdentifierQuery,
    searchByProductNameQuery,
    sorting,
    isDeleted,
  ]);

  // Trigger API call when state is mutated (with debounced search queries)
  useEffect(() => {
    if (isStateMutated) {
      // Validate minimum search length
      if (barcodeQuery?.length > 0 && barcodeQuery?.length < 3) {
        toast?.pushWarning(
          COMMON_VALIDATIONS.INPUT_LENGTH,
          PROVIDER_CONSTANT_PROPS.GENERIC.TOAST_TIME,
        );
        return;
      }
      if (
        productIdIdentifierQuery?.length > 0 &&
        productIdIdentifierQuery?.length < 3
      ) {
        toast?.pushWarning(
          COMMON_VALIDATIONS.INPUT_LENGTH,
          PROVIDER_CONSTANT_PROPS.GENERIC.TOAST_TIME,
        );
        return;
      }
      if (
        searchByProductNameQuery?.length > 0 &&
        searchByProductNameQuery?.length < 3
      ) {
        toast?.pushWarning(
          COMMON_VALIDATIONS.INPUT_LENGTH,
          PROVIDER_CONSTANT_PROPS.GENERIC.TOAST_TIME,
        );
        return;
      }
      const params = getApprovedProductParams();
      fetchApprovedProductsList(params);
      setIsStateMutated(false);
    }
  }, [
    smartCoolerProductsPagination,
    barcodeQuery,
    productIdIdentifierQuery,
    searchByProductNameQuery,
    sorting,
    isDeleted,
    isStateMutated,
  ]);

  const data = approvedProducts || [];

  const totalPages = Math.ceil(
    (paginationResponse?.totalRecords ?? 0) /
    smartCoolerProductsPagination?.limit,
  );

  // Use data directly from API (already sorted/filtered server-side)
  const paginatedData = data;

  const handlePageSizeChange = useCallback(
    (value: number) => {
      setSmartCoolerProductsPagination((prev) => ({ ...prev, limit: value }));
      if (totalPages < smartCoolerProductsPagination?.page) {
        setSmartCoolerProductsPagination({ page: totalPages, limit: value });
      }
    },
    [totalPages, smartCoolerProductsPagination?.page],
  );

  const handlePreviousClick = useCallback(
    (page: number) => {
      if (page > 1 && page <= totalPages) {
        setSmartCoolerProductsPagination((prev) => ({
          ...prev,
          page: page - 1,
        }));
      }
    },
    [totalPages],
  );

  const handleNextClick = useCallback(
    (page: number) => {
      if (page >= 1 && page < totalPages) {
        setSmartCoolerProductsPagination((prev) => ({
          ...prev,
          page: page + 1,
        }));
      }
    },
    [totalPages],
  );

  const onCopyClick = useCallback(() => {
    setIsCopy(true);
    setTimeout(() => setIsCopy(false), 500);
  }, []);

  const handlereset = useCallback(() => {
    setStartDate('');
    setStartEndDate('');
    onSearchByProductName('');
    onSearchByBarcode('');
    setProductIdIdentifier('');
    setIsOpen(false);
    setIsDeleted(false);
    setIsStateMutated(true);
  }, []);

  useEffect(() => {
    if (smartCoolerProductsPagination?.page > totalPages) {
      setSmartCoolerProductsPagination({
        ...smartCoolerProductsPagination,
        page: 1,
      });
    }
  }, [totalPages]);

  const { hasClicked, resetClickStatus } = useScreenClickDetector();

  useEffect(() => {
    return () => {
      if (toast && toast.clearAll) {
        toast.clearAll();
      }
    };
  }, []);

  useEffect(() => {
    if (hasClicked && toast) {
      toast.clearAll(); // Clear all toast popus when click is detected.
    }
    hasClicked && resetClickStatus();
  }, [hasClicked]);

  const isApplyButtonDisabled =
    createdStartDate &&
    createdEndDate &&
    new Date(createdEndDate) >= new Date(createdStartDate);
  const isError =
    createdStartDate !== '' &&
    createdEndDate !== '' &&
    createdStartDate > createdEndDate;

  function handleApply(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): void {
    event.preventDefault();
    setIsOpen(false);
  }

  const allowedFields = EAllowedFieldsForExport[EExportPage.SC] || [];

  const handleCloseErrorDialog = () => {
    deleteCookie(MAGIC_COOKIES.SID);
    deleteCookie(MAGIC_COOKIES.SIG);
    window.location.href = EStandalonePages.LANDING; // Redirect to login page
  };

  const columns = useMemo(
    () => buildColumns(isCopy, onCopyClick),
    [isCopy, onCopyClick],
  );

  // Dynamically generate heading with project name based on sub-application (if available)
  const generateHeading = () => {
    let subAppName = paginatedData[0]?.subapplication?.name;
    if (subAppName) {
      subAppName = subAppName.toLowerCase();
      const subAppInfo = SUBAPP_CONFIGS[subAppName];
      const profile =
        subAppInfo?.name === subAppName ? subAppInfo?.profile : '';
      return profile
        ? `${profile} ${STRINGS.STANDALONE_HEADER}`
        : STRINGS.STANDALONE_HEADER;
    }
    return STRINGS.STANDALONE_HEADER;
  };

  return (
    <>
      {/* Error Modal */}
      <ConfirmActionModal
        isOpen={!!apiError}
        onClose={handleCloseErrorDialog}
        onConfirm={handleCloseErrorDialog}
        title={STANDALONE_CONSTANTS.PRODUCTS_LOAD_ERROR_MODAL.TITLE}
        description={apiError}
        confirmLabel={
          STANDALONE_CONSTANTS.PRODUCTS_LOAD_ERROR_MODAL.INFO_BUTTON
        }
        variant="warning"
        size="xl"
      />

      {/* Authentication Error Modal */}
      <ConfirmActionModal
        isOpen={isAuthError}
        onClose={handleErrorClose}
        onConfirm={handleErrorClose}
        title={STANDALONE_CONSTANTS.ACCESS_ERROR_MODAL.TITLE}
        description={STANDALONE_CONSTANTS.ACCESS_ERROR_MODAL.DESCRIPTION}
        confirmLabel={STANDALONE_CONSTANTS.ACCESS_ERROR_MODAL.INFO_BUTTON}
        variant="warning"
        size="xl"
      />

      <FullPageCard
        pageClassName={`flex flex-col w-full h-full min-h-0 ${apiError || isAuthError ? 'filter blur-sm pointer-events-none' : ''}`}
        className="rounded-b-none"
        header={
          <PageToolbar
            title={generateHeading()}
            subtitle={STRINGS.STANDALONE_SUB}
            icon={getNavItemIcon(NAV_ITEMS.PRODUCTS)}
            count={paginationResponse?.totalRecords}
            actionsSlot={
              <div className="flex flex-wrap items-center gap-2 min-w-0 w-full md:flex-nowrap">
                <GlobalSearch
                  className="flex-1 min-w-[120px] md:flex-none md:w-[180px]"
                  placeholder={
                    APPROVED_PRODUCTS_PROPS.SEARCH.LEGACY_ID_PLACEHOLDER
                  }
                  globalSearch={productIdIdentifier}
                  setGlobalSearch={setProductIdIdentifier}
                  isDataFound={!isEmpty(paginatedData)}
                />
                <GlobalSearch
                  className="flex-1 min-w-[120px] md:flex-none md:w-[160px]"
                  placeholder={
                    APPROVED_PRODUCTS_PROPS.SEARCH.BARCODE_PLACEHOLDER
                  }
                  globalSearch={barcode}
                  setGlobalSearch={onSearchByBarcode}
                  isDataFound={!isEmpty(paginatedData)}
                />
                <GlobalSearch
                  className="flex-1 min-w-[120px] md:flex-none md:w-[180px]"
                  placeholder={
                    APPROVED_PRODUCTS_PROPS.SEARCH.PRODUCT_NAME_PLACEHOLDER
                  }
                  globalSearch={searchByProductName}
                  setGlobalSearch={onSearchByProductName}
                  isDataFound={!isEmpty(paginatedData)}
                />
              </div>
            }
          />
        }
      >
        <DataTable
          data={paginatedData}
          columns={columns}
          loading={isLoading}
          skeletonRows={smartCoolerProductsPagination.limit}
          onSort={(accessor, order) => setSorting({ orderBy: accessor, order })}
          sortState={{ accessor: sorting.orderBy, order: sorting.order as any }}
          pagination={
            !isEmpty(paginatedData)
              ? {
                state: smartCoolerProductsPagination,
                totalItem: paginationResponse?.totalRecords,
                rowsPerPageOptions: ROW_PER_PAGE_OPTION,
                onRowsPerPageChange: handlePageSizeChange,
                onNextClick: handleNextClick,
                onPreviousClick: handlePreviousClick,
              }
              : null
          }
          rowKey={(item, idx) => item.smartCoolerProductId ?? String(idx)}
          rowTestId={(item: any, idx: number) =>
            tid(TID.TABLE_ROW(`${idx}`))
          }
        />
      </FullPageCard>
    </>
  );
};
export default ApprovedProducts;
